export function makeEmptyRow(keys) {
  return keys.reduce((acc, k) => {
    acc[k] = '';
    return acc;
  }, {});
}

/** PoC 지원서: 추진배경·목표 및 내용·기대효과만 장문(섹션형·textarea) 처리 */
const POC_LONG_TEXT_KEYS_NORMALIZED = new Set([
  'background',
  'goalsandcontent',
  'expectedeffect',
  'expectedeffects',
]);

function normalizedFieldKeyForPocLongText(fieldKey) {
  return String(fieldKey || '')
    .replace(/_/g, '')
    .toLowerCase();
}

export function isLongTextField(fieldKey = '', _label = '') {
  return POC_LONG_TEXT_KEYS_NORMALIZED.has(normalizedFieldKeyForPocLongText(fieldKey));
}

/** 생년월일 필드 여부 */
export function isBirthDateField(fieldKey = '', label = '') {
  const k = String(fieldKey).toLowerCase();
  const l = String(label);
  return k === 'birth_date' || k === 'birthdate' || /생년월일/.test(l);
}

/** 날짜 선택 입력란으로 렌더할지 여부 (키/라벨 패턴으로 판별) */
export function isDateField(fieldKey = '', label = '') {
  const k = String(fieldKey).toLowerCase();
  const l = String(label);
  return (
    /_date$|^date_|_date_/.test(k) ||
    /제출일|가입일|작성일|날짜|일자|지출예정일자/.test(l)
  );
}

/** 금액·숫자 입력란으로 렌더할지 여부 (키/라벨 패턴으로 판별) */
export function isNumberField(fieldKey = '', label = '') {
  const k = String(fieldKey).toLowerCase();
  const l = String(label);
  return (
    /amount|_count|count_|number|num_|_num/.test(k) ||
    /금액|인원\s*수|숫자|예상금액|금액|수량|개수/.test(l)
  );
}

/** 연락처 입력란 여부 (키/라벨 패턴) */
export function isPhoneField(fieldKey = '', label = '') {
  const k = String(fieldKey).toLowerCase();
  const l = String(label);
  return (
    /phone|_phone|연락처/.test(k) ||
    /연락처|휴대폰|전화/.test(l)
  );
}

/** 이메일 입력란 여부 (키/라벨 패턴) */
export function isEmailField(fieldKey = '', label = '') {
  const k = String(fieldKey).toLowerCase();
  const l = String(label);
  return /email|e-mail|e_mail|useremail|user_email/.test(k) || /이메일|메일/.test(l);
}

/** 이메일 형식 검사(간단) */
export function isValidEmailFormat(value) {
  const s = String(value ?? '').trim();
  if (!s) return true; // 빈 값은 형식 에러로 보지 않음(필수 여부는 별도 검증)
  // 실무용 간단 검증: 공백 금지 + @ 앞/뒤 존재 + 도메인에 dot 포함
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

/**
 * 레이아웃·layoutRows에서 이메일 필드 key → 값 맵 추출 (내 정보: 최초 이메일 스냅샷용)
 */
export function collectEmailFieldsFromLayout(layout, layoutRows) {
  const emails = {};
  if (!Array.isArray(layout)) return emails;

  const walk = (items, row) => {
    if (!Array.isArray(items)) return;
    for (const item of items) {
      if (!item) continue;
      const nested = item.fields || item.items;
      if (item.type === 'SECTION' || item.type === 'ROW') {
        walk(nested, row);
        continue;
      }
      if (item.type === 'GROUP' && Array.isArray(item.fields)) {
        walk(item.fields, row);
        continue;
      }
      if (item.key && isEmailField(item.key, item.label)) {
        emails[item.key] = String(row?.[item.key] ?? '').trim();
      }
    }
  };

  layout.forEach((block, sectionIdx) => {
    const rows = layoutRows?.[sectionIdx];
    const row = Array.isArray(rows) && rows.length > 0 ? rows[0] : {};
    const items = block?.type === 'DIVIDER' ? block.items : block?.fields;
    walk(items, row);
  });
  return emails;
}

/** 내 정보 수정: 이메일이 최초 값과 다르면 인증 완료(verified) 필요 */
export function isEmailChangeRequiringVerification(fieldKey, email, status, originalEmailsByField) {
  const trimmed = String(email ?? '').trim();
  if (!trimmed) return false;
  const original = String(originalEmailsByField?.[fieldKey] ?? '').trim();
  if (trimmed === original) return false;
  return status !== 'verified';
}

/** 연락처 입력: 숫자, 공백, 하이픈, +, 괄호만 허용 */
export function sanitizePhoneInput(value) {
  if (value == null || value === '') return '';
  const s = String(value);
  return s.replace(/[^\d\s\-+()]/g, '');
}

/** 연락처 포맷 (포커스 아웃 시): 010-0000-0000, 02-123-4567 등 */
export function formatPhoneForDisplay(value) {
  const digits = String(value == null ? '' : value).replace(/\D/g, '');
  if (digits.length === 0) return '';
  if (digits.startsWith('010') && digits.length === 11) {
    return `010-${digits.slice(3, 7)}-${digits.slice(7)}`;
  }
  if (digits.startsWith('02') && (digits.length === 9 || digits.length === 10)) {
    return digits.length === 9 ? `02-${digits.slice(2, 5)}-${digits.slice(5)}` : `02-${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  if (digits.length >= 10 && digits.startsWith('0') && digits.length <= 11) {
    const area = digits.slice(0, 3);
    const rest = digits.slice(3);
    if (rest.length <= 4) return `${area}-${rest}`;
    return `${area}-${rest.slice(0, rest.length - 4)}-${rest.slice(-4)}`;
  }
  return digits;
}

/** 숫자 입력란용: 숫자·소수점·선택적 맨 앞 마이너스만 허용, 나머지 제거 */
export function sanitizeNumberInput(value) {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  let hasDot = false;
  const out = [];
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === '-' && out.length === 0) {
      out.push(c);
    } else if (c === '.' && !hasDot) {
      hasDot = true;
      out.push(c);
    } else if (/\d/.test(c)) {
      out.push(c);
    }
  }
  return out.join('');
}

export function fieldColClass(fieldKey = '', label = '') {
  if (isLongTextField(fieldKey, label)) return 'col-12';
  return 'col-12 col-md-6';
}

export function flattenFirstRows(layoutRows) {
  const merged = {};
  Object.keys(layoutRows || {}).forEach((k) => {
    const idx = Number(k);
    const rows = Array.isArray(layoutRows?.[idx]) ? layoutRows[idx] : [];
    if (!rows.length) return;
    const row0 = rows[0] || {};
    Object.keys(row0).forEach((key) => {
      merged[key] = row0[key];
    });
  });
  return merged;
}

/** layoutConfig.inputMode (또는 input_mode): EDITABLE | READONLY | DISABLED */
export function getLayoutInputMode(field) {
  const raw = field?.inputMode ?? field?.input_mode;
  return String(raw ?? 'EDITABLE').toUpperCase().replace(/[\s-]/g, '_');
}

/**
 * 폼 전체 조회 전용(readOnlyForm) + 필드(및 선택적 ARRAY 부모)의 inputMode 를 합쳐 입력 위젯 상태 반환.
 * - 조회 전용: 기존과 동일하게 읽기 전용 + date/text 일부는 disabled 까지 사용
 * - READONLY: 값은 제출되며 포커스/복사 가능
 * - DISABLED: 비활성(네이티브 폼에서는 제출 제외)
 */
export function resolveFieldInputState(formReadOnly, field, parentField = null) {
  if (formReadOnly) {
    return { useReadOnly: true, useDisabled: true, nonInteractive: true };
  }
  if (parentField) {
    const pm = getLayoutInputMode(parentField);
    if (pm === 'READONLY') return { useReadOnly: true, useDisabled: false, nonInteractive: true };
    if (pm === 'DISABLED') return { useReadOnly: false, useDisabled: true, nonInteractive: true };
  }
  const mode = getLayoutInputMode(field);
  if (mode === 'READONLY') return { useReadOnly: true, useDisabled: false, nonInteractive: true };
  if (mode === 'DISABLED') return { useReadOnly: false, useDisabled: true, nonInteractive: true };
  return { useReadOnly: false, useDisabled: false, nonInteractive: false };
}

/** ARRAY 필드 정의에 READONLY/DISABLED 가 있으면 행 추가·삭제 등 구조 액션 숨김 */
export function isArrayParentStructureLocked(arrayFieldDef) {
  if (!arrayFieldDef) return false;
  const m = getLayoutInputMode(arrayFieldDef);
  return m === 'READONLY' || m === 'DISABLED';
}

